import pLimit from 'p-limit'
import { create, toJson } from '@bufbuild/protobuf'
import type { Transport } from '@connectrpc/connect'
import {
  callUnaryMethod,
  createConnectQueryKey,
  useQuery as useConnectQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { type QueryClient, useQuery } from '@tanstack/react-query'

import { CollectionRefSchema } from '@jotta/grpc-connect-openapi/collection'
import type { FieldMask } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/mask_pb'
import type { GeoCoordinates } from '@jotta/grpc-connect-openapi/location'
import type {
  GetPhotoRequest,
  Photo,
  Photo_PhotoAction,
  Photo_State,
  PhotoRef,
} from '@jotta/grpc-connect-openapi/photo'
import {
  GetPhotoRequestSchema,
  PhotoRefSchema,
} from '@jotta/grpc-connect-openapi/photo'
import { getPhoto } from '@jotta/grpc-connect-openapi/photoQuery'
import type { PartialMessage } from '@jotta/types/compat'
import type { PhotosApi } from '@jotta/types/PhotosApi'

import { usePhotoCollectionService } from '../components/PhotoGallery/timelinev2/useTimelineStream'
import { useMediaObjectStore } from '../store/PhotoContext'
import type { SelectablePhoto } from '../store/SelectionStore'

import { fetchPhotoById } from './photosApi'
import type { CollectionRef } from './useCollection'

const mask: PartialMessage<FieldMask> = {
  includes: [
    {
      fieldNumber: 1, // photo
      childMask: {
        includes: [
          { fieldNumber: 1 }, // idDeprecated
          { fieldNumber: 2 }, // md5
          { fieldNumber: 3 }, // state
          { fieldNumber: 4 }, // ref
          { fieldNumber: 5 }, // expiringSmallThumbUrl
          { fieldNumber: 6 }, // timestampNanos
          { fieldNumber: 7 }, // videoDurationSeconds
          { fieldNumber: 8 }, // filesize
          { fieldNumber: 9 }, // fileName
          { fieldNumber: 10 }, // Width
          { fieldNumber: 11 }, // Height
          { fieldNumber: 12 }, // location
          { fieldNumber: 13 }, // camera
          { fieldNumber: 14 }, // iso
          { fieldNumber: 15 }, // focalLength
          { fieldNumber: 16 }, // exposure
          { fieldNumber: 17 }, // owner
          { fieldNumber: 19 }, // encodedContentRef
          { fieldNumber: 20 }, // ownerFullName
          { fieldNumber: 21 }, // isLivePhoto
          { fieldNumber: 23 }, // actions
        ],
      },
    },
    { fieldNumber: 2 }, // commentItemId,
  ],
}
type CollectionPhotoAndComment = {
  commentItemId?: string
  photo?: {
    idDeprecated: string
    md5: string
    state: Photo_State
    ref?: PhotoRef
    timestampNanos: bigint
    videoDurationSeconds?: number
    expiringSmallThumbUrl: string
    fileSize: bigint
    fileName: string
    width: number
    height: number
    location?: GeoCoordinates
    camera?: string
    iso?: number
    focalLength?: string
    exposure?: string
    owner: string
    encodedContentRef: string
    ownerFullName: string
    isLivePhoto: boolean
    actions: Photo_PhotoAction[]
  }
}

export const photoStaleTime = 600_000
const fetchPhotoLimit = pLimit(4) // low limit due to fetching legacy photos with http 1.1

export type ResolvedSelectablePhoto = Photo & SelectablePhoto

export function photoInCollectionQueryKey(
  collectionRef: CollectionRef,
  id: string | undefined,
) {
  return [
    'photo',
    'richV2',
    {
      collectionRef: collectionRef.ref?.case
        ? toJson(
            CollectionRefSchema,
            create(CollectionRefSchema, collectionRef),
          )
        : null,
      id,
    },
  ] as const
}

export function photoRefFromMd5(md5: string): PhotoRef {
  return create(PhotoRefSchema, {
    ref: {
      case: 'md5',
      value: md5,
    },
  })
}

function getPhotoRequest(ref: PhotoRef): GetPhotoRequest {
  if (!ref.ref.case) {
    throw new Error('Photo ref has no queryable value')
  }

  return create(GetPhotoRequestSchema, {
    ref,
  })
}

function toSelectablePhoto(photo: Photo, fallbackRef?: PhotoRef) {
  return {
    ...photo,
    id: photo.idDeprecated,
    ref: photo.ref ?? fallbackRef,
  }
}

export function photoByRefQueryOptions(transport: Transport, ref: PhotoRef) {
  const request = getPhotoRequest(ref)

  return {
    queryKey: createConnectQueryKey({
      schema: getPhoto,
      input: request,
      transport,
      cardinality: 'finite',
    }),
    queryFn: async ({ signal }: { signal?: AbortSignal }) =>
      callUnaryMethod(transport, getPhoto, request, { signal }),
    staleTime: photoStaleTime,
  }
}

export async function resolveSelectablePhoto(
  transport: Transport,
  queryClient: QueryClient,
  photo: SelectablePhoto,
): Promise<ResolvedSelectablePhoto> {
  const ref = photo.ref?.ref.case
    ? photo.ref
    : photo.md5
      ? photoRefFromMd5(photo.md5)
      : undefined

  if (!ref) {
    throw new Error(`Photo ${photo.id} has no resolvable photo ref`)
  }

  const resolvedPhoto = await queryClient.ensureQueryData(
    photoByRefQueryOptions(transport, ref),
  )

  if (!resolvedPhoto.photo) {
    throw new Error(`Photo ${photo.id} returned no photo`)
  }

  return toSelectablePhoto(resolvedPhoto.photo, ref)
}

export function usePhotoRef(
  ref: PhotoRef | undefined,
  options?: {
    enabled?: boolean
    throwOnError: boolean
  },
) {
  return useConnectQuery(getPhoto, ref ? getPhotoRequest(ref) : undefined, {
    enabled: Boolean((options?.enabled ?? true) && ref),
    staleTime: photoStaleTime,
    select: response =>
      response.photo ? toSelectablePhoto(response.photo, ref) : undefined,
    throwOnError: options?.throwOnError,
  })
}

export function usePhoto(
  collectionRef: CollectionRef,
  itemMap: { [photoId: string]: PhotosApi.Media },
  id?: string,
  {
    enabled = true,
    throwOnError = true,
  }: { enabled?: boolean; throwOnError?: boolean } = {},
) {
  const client = usePhotoCollectionService()
  const transport = useTransport()
  const theId = id || ''
  const legacyPhoto = theId ? itemMap[theId] : undefined
  const canFetchLegacyPhotoById = collectionRef.ref?.case !== 'shareId'

  return useQuery({
    queryKey: photoInCollectionQueryKey(collectionRef, id),
    queryFn: async ({ signal }) => {
      const photo = await fetchRichV2Photo(
        collectionRef,
        legacyPhoto ?? (await fetchPhotoById(theId)),
        client,
        transport,
        signal,
      )

      return photo
    },
    staleTime: photoStaleTime,
    enabled: Boolean(enabled && id && (legacyPhoto || canFetchLegacyPhotoById)),
    refetchOnMount: false,
    throwOnError: throwOnError,
  }).data
}

async function fetchRichV2Photo(
  collectionRef: CollectionRef,
  legacyPhoto: PhotosApi.Media,
  client: ReturnType<typeof usePhotoCollectionService>,
  transport: ReturnType<typeof useTransport>,
  signal?: AbortSignal,
) {
  const timestampString = legacyPhoto?.timestamp
  const timestamp = timestampString ? BigInt(timestampString) : undefined

  let photoAndComment: CollectionPhotoAndComment | undefined = undefined
  if (collectionRef.ref?.case === 'wellKnownCollection') {
    photoAndComment = {
      photo: (
        await callUnaryMethod(
          transport,
          getPhoto,
          getPhotoRequest(photoRefFromMd5(legacyPhoto.md5)),
          {
            signal,
          },
        )
      ).photo,
    }
  } else {
    // TODO very inefficient to do two lookups of the photo, but in time we can feed this hooks with
    // a photo ref and do a single lookup instead. Then we can also probably discard the item map cache
    // and rely on tanstack managing the state instead
    const collectionPhotosMasked = client.getCollectionPhotosMasked(
      {
        collectionRef,
        mask,
        limit: 1,
        selection: {
          case: 'scan',
          value: {
            start: timestamp
              ? {
                  case: 'startInclusiveTimestampNanos',
                  value: timestamp,
                }
              : undefined,
          },
        },
      },
      {
        signal,
      },
    )
    const iterator = collectionPhotosMasked[Symbol.asyncIterator]()
    photoAndComment = (await iterator.next()).value
  }

  const photo = photoAndComment?.photo
  return legacyPhoto &&
    photoAndComment &&
    photo &&
    legacyPhoto.id === photo.idDeprecated
    ? {
        ...photo,
        commentItemId: photoAndComment.commentItemId,
        ref: photo.ref,

        // legacy field kept for now
        id: photo.idDeprecated,
        file_url: legacyPhoto.file_url,
      }
    : undefined
}

export type CarouselPhoto = NonNullable<ReturnType<typeof usePhoto>>

/**
 *  @deprecated
 *  Using the photo fetcher to fetch potentially large collections of photos (such as 40k single-day selections) is not recommended.
 *  The long-term plan is to retain richer photo objects instead of just photo ids so we don't have to re-fetch a rich photo later.
 */
export function usePhotoFetcher(collectionRef: CollectionRef) {
  const photoCollectionService = usePhotoCollectionService()
  const transport = useTransport()
  const mediaStore = useMediaObjectStore()

  function fetchPhoto(photoId: string) {
    return fetchPhotoLimit(async () => {
      const legacyPhoto =
        mediaStore.getPhoto(photoId) ?? (await fetchPhotoById(photoId))
      const richPhoto = await fetchRichV2Photo(
        collectionRef,
        legacyPhoto,
        photoCollectionService,
        transport,
      )
      if (!richPhoto) {
        throw new Error(`Photo with id ${photoId} not found`)
      }
      return richPhoto
    })
  }

  return {
    fetchPhoto,
    fetchAll: (photoIds: readonly string[]) =>
      Promise.all(photoIds.map(fetchPhoto)),
  }
}

/** @deprecated see above */
export function usePhotos(
  collectionRef: CollectionRef,
  photoIds: readonly string[],
  { enabled = true }: { enabled?: boolean } = {},
) {
  const { fetchAll } = usePhotoFetcher(collectionRef)
  return (
    useQuery({
      queryKey: ['usePhotos', ...photoIds],
      queryFn: () => fetchAll(photoIds),
      enabled,
      throwOnError: false, // See MediaCarousel for why we sometimes cannot fetch photos reliably
    }).data || []
  )
}
